import style from "./LanguageSwitcher.module.css";
import { useTranslation } from "react-i18next";
import Icon from "@/ui/icon/Icon";
import FlagIcon from "@/features/forms/components/FlagIcon";
import { useLanguage } from "@/i18n/useLanguage";
import { LANGUAGES, LANGUAGE_NAMES } from "@/i18n/language";

// Each language is named in itself, so someone who cannot read the current one
// can still find theirs. No flags: a flag stands for a country, not a language.
//
// Variants:
//   toggle     globe + the other language's name, one click to switch
//              (header, auth pages). With two languages a menu is an extra
//              click for nothing, and naming the target says what happens.
//   links      "Bosanski · English" (footer)
//   options    two radio cards, flag + name + hint (settings; sits inside
//              a <form>)
// The same flag set as the certificate-language dropdown below it, keyed the
// way FlagIcon expects.
const FLAG_CODES = { bs: "BA", en: "EN" };

export default function LanguageSwitcher({ variant = "toggle", className = "" }) {
	const { language, changeLanguage, enabled } = useLanguage();
	const { t } = useTranslation();

	if (!enabled) return null;

	if (variant === "options") {
		// Real radio inputs, so arrow keys and screen readers behave as for
		// any radio group. They carry no form name of their own: choosing one
		// applies and saves the language at once, it is not part of "Sačuvaj".
		return (
			<div
				role="radiogroup"
				aria-label={t("common:language.label")}
				className={`${style.options} ${className}`}
			>
				{LANGUAGES.map((option) => (
					<label
						key={option}
						className={`${style.option} ${option === language ? style.selected : ""}`}
					>
						<input
							type="radio"
							className={style.radio}
							checked={option === language}
							onChange={() => changeLanguage(option)}
						/>
						<span className={style.flag} aria-hidden="true">
							<FlagIcon code={FLAG_CODES[option]} />
						</span>
						{/* Name and hint are each in their own language, so every
						    reader finds the option written for them. */}
						<span className={style.text} lang={option}>
							<span className={style.name}>{LANGUAGE_NAMES[option]}</span>
							<span className={style.hint}>
								{t("settings:language.hint", { lng: option })}
							</span>
						</span>
						<Icon
							icon={option === language ? "fa-circle-check" : "fa-circle"}
							iconType={option === language ? "fa-solid" : "fa-light"}
							className={style.indicator}
						/>
					</label>
				))}
			</div>
		);
	}

	if (variant === "links") {
		return (
			<div
				role="group"
				aria-label={t("common:language.label")}
				className={`${style[variant]} ${className}`}
			>
				{LANGUAGES.map((option) => (
					<button
						key={option}
						type="button"
						lang={option}
						aria-pressed={option === language}
						className={option === language ? style.active : ""}
						onClick={() => changeLanguage(option)}
					>
						{LANGUAGE_NAMES[option]}
					</button>
				))}
			</div>
		);
	}

	const other = LANGUAGES.find((option) => option !== language);

	return (
		<button
			type="button"
			className={`${style.toggle} ${className}`}
			onClick={() => changeLanguage(other)}
		>
			{/* Light, not regular: the globe's many strokes read heavier than
			    the header's other icons at the same weight. */}
			<Icon icon="fa-globe" iconType="fa-light" />
			<span lang={other} className={style.toggleLabel}>
				{LANGUAGE_NAMES[other]}
			</span>
		</button>
	);
}
